import React, { useState, useRef, useEffect, useMemo } from "react";
import StatusBarDropdown from "./StatusBarDropdown";
import PendingUndoChip from "./PendingUndoChip";
import { X, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { ACTIONS, getStatusLabel, getStatusValue } from "../constants/actions";

const getStatusId = (status) => {
  if (!status || typeof status === "string") return "";
  return status.id || status.status_id || "";
};

const getStatusKey = (status) => getStatusValue(status).toLowerCase();

const getActionMeta = (status) => {
  const statusKey = getStatusKey(status);
  return ACTIONS.find((a) => a.status === statusKey) || null;
};

export const StatusBar = ({
  currentStatus,
  updateStatus,
  loading,
  theme,
  disabled,
  allowedStatus,
  onUndo,
  canUndo = false,
  undoLabel = "Undo last status change",
  hasPendingUndo = false,
  onOpenPendingUndo,
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [frozenStatus, setFrozenStatus] = useState(null);
  const closeTimer = useRef(null);

  const isLocked = !!loading || !!disabled;

  const currentStatusId = getStatusId(currentStatus);
  const normalizedCurrentStatus = getStatusKey(currentStatus);
  const effectiveStatus = frozenStatus || currentStatus;

  const currentAction = useMemo(
    () => getActionMeta(effectiveStatus),
    [effectiveStatus]
  );

  const mobileOptions = useMemo(() => {
    if (!Array.isArray(allowedStatus)) return [];

    return allowedStatus.filter((item) => {
      const itemId = getStatusId(item);
      const itemKey = getStatusKey(item);

      if (currentStatusId && itemId) {
        return itemId !== currentStatusId;
      }

      return itemKey !== normalizedCurrentStatus;
    });
  }, [allowedStatus, currentStatusId, normalizedCurrentStatus]);

  const handleStatusClick = (statusItem) => {
    if (isLocked) return;

    setFrozenStatus(currentStatus);
    setMobileMenuOpen(false);

    updateStatus({
      id: getStatusId(statusItem),
      code: statusItem?.code || "",
      name: getStatusLabel(statusItem),
      raw: statusItem,
    });

    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setFrozenStatus(null), 350);
  };

  useEffect(() => {
    return () => clearTimeout(closeTimer.current);
  }, []);

  const displayStatus = getStatusLabel(effectiveStatus);

  // A pending undo request locks the status server-side, so the control is the
  // marker that opens the request rather than a menu of moves that would be
  // refused.
  if (hasPendingUndo) {
    return (
      <>
        <div className="flex w-full md:hidden">
          <PendingUndoChip currentStatus={currentStatus} onOpen={onOpenPendingUndo} className="w-full justify-center py-3" />
        </div>
        <div className="hidden md:block">
          <PendingUndoChip currentStatus={currentStatus} onOpen={onOpenPendingUndo} />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex w-full items-center gap-2 md:hidden">
        <div
          className={`flex flex-1 min-w-0 items-center justify-center gap-1.5 rounded-full bg-button-secondary px-4 py-3 text-sm font-medium text-white select-none ${
            isLocked ? "cursor-not-allowed opacity-50" : "cursor-default"
          }`}
        >
          {currentAction?.icon && (
            <currentAction.icon
              size={16}
              style={{ color: currentAction.iconColor }}
              aria-hidden="true"
            />
          )}

          <span className="truncate">{displayStatus}</span>
        </div>

        <Button
          onClick={() =>
            !isLocked && (mobileOptions.length > 0 || canUndo) && setMobileMenuOpen(true)
          }
          disabled={isLocked || (mobileOptions.length === 0 && !canUndo)}
          aria-label="More actions"
          className={`flex h-11 w-11 items-center justify-center rounded-full border border-border-primary bg-background-form p-0 text-text-primary transition-opacity ${
            isLocked || (mobileOptions.length === 0 && !canUndo)
              ? "cursor-not-allowed opacity-50"
              : "cursor-pointer"
          }`}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <circle cx="12" cy="5" r="1.5" />
            <circle cx="12" cy="12" r="1.5" />
            <circle cx="12" cy="19" r="1.5" />
          </svg>
        </Button>
      </div>

      {mobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50 md:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />

          <div
            className="fixed bottom-0 left-0 right-0 z-50 rounded-t-2xl bg-background-form shadow-xl md:hidden"
            style={{ animation: "slideUp 250ms ease-out" }}
          >
            <div className="flex items-center justify-between px-5 pb-3 pt-5">
              <span className="text-sm font-semibold text-text-primary">
                Candidate Actions
              </span>

              <Button
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close"
                className="flex h-7 w-7 items-center justify-center rounded-full bg-background-secondary p-0 text-text-muted"
              >
                <X size={14} />
              </Button>
            </div>

            <div className="flex flex-col gap-2 px-4 pb-8">
              {canUndo && !isLocked && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onUndo?.();
                  }}
                  className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left transition-colors hover:bg-warning/10 text-text-primary"
                >
                  <RotateCcw size={20} className="text-warning" />
                  <span className="text-sm font-medium">{undoLabel}</span>
                </Button>
              )}

              {mobileOptions.map((statusItem, index) => {
                const action = getActionMeta(statusItem);
                const Icon = action?.icon;

                return (
                  <Button
                    key={
                      statusItem?.id ||
                      statusItem?.status_id ||
                      statusItem?.code ||
                      statusItem?.name ||
                      index
                    }
                    onClick={() => handleStatusClick(statusItem)}
                    disabled={isLocked}
                    className={`flex w-full items-center gap-3 rounded-xl bg-background-secondary px-4 py-3 text-left transition-colors hover:bg-background-hover ${
                      isLocked ? "cursor-not-allowed opacity-40" : "cursor-pointer"
                    }`}
                  >
                    {Icon && (
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
                        <Icon
                          size={20}
                          style={{ color: action?.iconColor }}
                          aria-hidden="true"
                        />
                      </div>
                    )}

                    <span className="text-sm font-medium text-text-primary">
                      {getStatusLabel(statusItem)}
                    </span>
                  </Button>
                );
              })}
            </div>
          </div>

          <style>{`
            @keyframes slideUp {
              from { transform: translateY(100%); }
              to { transform: translateY(0); }
            }
          `}</style>
        </>
      )}

      <div className="hidden md:block">
        <StatusBarDropdown
          currentStatus={currentStatus}
          updateStatus={updateStatus}
          loading={loading}
          theme={theme}
          disabled={disabled}
          allowedStatus={allowedStatus}
          onUndo={onUndo}
          canUndo={canUndo}
          undoLabel={undoLabel}
        />
      </div>
    </>
  );
};